import type { ReadonlyRefOrGetter } from "@vueuse/core";
import type { Price } from "mws-ts-v2-sdk";

import type { IsoCurrencyCode } from "~/types/Misc";

/**
 * @param amountValue - a dictionary of prices keyed by ISO currency code
 * @returns the price in the most suitable currency, the currency code, and
 * flag isActiveCurrencyAvailable
 *
 * @desc this composable hooks into activeCurrency, defaultCurrency, and locale
 * and returns the price for products in a currency with this preference order:
 * 1. activeCurrency
 * 2. platformDefaultCurrency
 * 3. Euros
 */
export const usePrice = (amount: ReadonlyRefOrGetter<Price[]>) => {
  const { activeCurrency } = useActiveCurrency();
  const activeCurrencyCode = computed(
    () => activeCurrency.value.isoCurrencyCode,
  );

  const platformStore = usePlatformStore();
  const platformDefaultCurrencyCode = computed(
    () => platformStore.getDefaultCurrency.isoCurrencyCode,
  );

  const amountWithFallback = computed(() => {
    return getAmountWithFallbacks(
      toValue(amount),
      activeCurrencyCode.value,
      platformDefaultCurrencyCode.value,
    );
  });

  return { amount: amountWithFallback };
};

export const useIsActiveCurrencyMissing = (
  productPrices: ReadonlyRefOrGetter<Price[]>,
) => {
  const { activeCurrency } = useActiveCurrency();

  return computed(() =>
    isPriceInCurrencyNotAvailable(
      toValue(productPrices),
      activeCurrency.value.isoCurrencyCode,
    ),
  );
};

/**
 *
 * @param amount - a dictionary, keyed by ISO currency code, of money
 * @param activeCurrencyCode - an ISO currency code for looking up the money value
 * @param defaultCurrencyCode - an ISO currency code for looking up the money value
 * @returns a dictionary of money with keys for value and currencyCode, if there is
 * no value for either the activeCurrency or defaultCurrency, then it returns
 * {key: EUR, value: amount.EUR }
 */
const getAmountWithFallbacks = (
  price: Price[],
  activeCurrencyCode: IsoCurrencyCode,
  defaultCurrencyCode: IsoCurrencyCode,
) => {
  // if the price in activeCurrency is NOT available...
  if (isPriceInCurrencyNotAvailable(price, activeCurrencyCode)) {
    // ... and the price in defaultCurrency is NOT available...
    if (
      defaultCurrencyCode &&
      isPriceInCurrencyNotAvailable(price, defaultCurrencyCode)
    ) {
      // ... then return Euros
      return {
        value: getPriceInCurrency(price, "EUR"),
        currencyCode: "EUR",
      };
    }

    // ... but if the price in defaultCurrency IS available, return it
    if (defaultCurrencyCode) {
      return {
        value: getPriceInCurrency(price, defaultCurrencyCode),
        currencyCode: defaultCurrencyCode,
      };
    }
  }

  // return price in activeCurrency when possible
  return {
    value: getPriceInCurrency(price, activeCurrencyCode),
    currencyCode: activeCurrencyCode,
  };
};

export const useDiscountPrice = (
  price: MaybeRefOrGetter<number>,
  discountPercentage: MaybeRefOrGetter<number | undefined>,
) => {
  const discountValue = computed(() => {
    return toValue(discountPercentage) ?? 0;
  });

  return {
    price: computed(() =>
      Math.floor(
        toValue(price) - (toValue(price) * toValue(discountValue)) / 100,
      ),
    ),
    available: computed(() => {
      return toValue(discountValue) > 0;
    }),
  };
};
